<template>
  <div class="business-subscription-manager">
    <div v-if="loading" class="loading">Loading...</div>
    <div v-else>
      <div v-if="currentSubscription && currentSubscription.length">
        <h3 class="title">Active Subscriptions</h3>
        <div class="subscriptions-list">
          <div v-for="subscription in currentSubscription" :key="subscription.id" class="subscription-card">
            <div class="card-header">
              <span class="plan-name">{{ subscription.agency_subscription.name }}</span>
              <span class="status-badge" :class="subscription.status">{{ subscription.status }}</span>
            </div>
            <div class="card-body">
              <p class="description">{{ subscription.agency_subscription.description }}</p>
              <div class="details-grid">
                <div><strong>Start:</strong> {{ formatDate(subscription.starts_at) }}</div>
                <div v-if="subscription.ends_at"><strong>End:</strong> {{ formatDate(subscription.ends_at) }}</div>
                <div><strong>Amount:</strong> ${{ formatAmount(subscription.agency_subscription.amount) }} / {{ subscription.agency_subscription.interval }}</div>
                <div><strong>Agency:</strong> {{ subscription.agency.name }}</div>
              </div>
              <div class="modules-access">
                <strong>Access:</strong>
                <div class="modules-list">
                  <span v-for="module in subscription.agency_subscription.modules" :key="module" class="module-badge">
                    {{ moduleName(module) }}
                  </span>
                </div>
              </div>
            </div>
            <div v-if="subscription.status === 'active'" class="card-footer">
              <button class="cancel-btn" :disabled="cancelling" @click="openCancelModal(subscription)">Cancel Subscription</button>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <p class="no-subscription">No active subscription.</p>
      </div>
    </div>

    <!-- Cancel Confirmation Modal -->
    <div v-if="showCancelModal" class="modal-overlay">
      <div class="modal-content">
        <h4>Cancel Subscription</h4>
        <p>Are you sure you want to cancel the subscription for <strong>{{ pendingCancelSubscription?.agency_subscription?.name }}</strong>?</p>
        <div class="modal-actions">
          <button class="modal-btn danger" :disabled="cancelling" @click="confirmCancelSubscription">Yes, Cancel</button>
          <button class="modal-btn" :disabled="cancelling" @click="closeCancelModal">No, Keep</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'BusinessSubscriptionManager',
  data() {
    return {
      loadingSubscription: null,
      selectedPlan: null,
      showPaymentModal: false,
      pendingCancelSubscription: null,
      showCancelModal: false,
    };
  },
  computed: {
    ...mapGetters('businessSubscription', [
      'loading',
      'cancelling',
      'subscribingId',
      'currentSubscription',
      'availableSubscriptions',
    ]),
    user() {
      return this.$store.getters['auth/user'];
    },
  },
  created() {
    this.$store.dispatch('businessSubscription/fetchCurrentSubscription');
  },
  methods: {
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    formatAmount(amount) {
      return Number(amount).toFixed(2);
    },
    subscribeTo(plan) {
      this.$store.dispatch('businessSubscription/subscribeTo', plan)
        .then(() => {
          let that = this;
          setTimeout(function () {
            that.loadingSubscription = null;
            window.location.reload();
          }, 3000);
        })
        .catch(() => {
          this.loadingSubscription = null;
        });
    },
    openCancelModal(subscription) {
      this.pendingCancelSubscription = subscription;
      this.showCancelModal = true;
    },
    closeCancelModal() {
      this.pendingCancelSubscription = null;
      this.showCancelModal = false;
    },
    confirmCancelSubscription() {
      if (!this.pendingCancelSubscription) return;
      try {
        this.$store.dispatch('businessSubscription/cancelSubscription', { pendingSubscriptionId: this.pendingCancelSubscription.id })
          .then(() => {
            this.$store.dispatch('businessSubscription/fetchCurrentSubscription');
            this.$toast && this.$toast.success('Subscription cancelled');
          })
          .catch((e) => {
            this.loadingSubscription = null;
            this.$toast.error(e.response.data.error);
          });
      } catch (e) {
        this.$toast && this.$toast.error('Failed to cancel subscription.');
      }
      this.closeCancelModal();
    },
    moduleName(key) {
      const map = {
        has_re: 'AI Review Reply',
        has_te: 'NFC Reviews',
        has_social_review: 'Social Review Posting',
        has_social_fusion: 'AI Social Media Posts',
        has_review_requester: 'Email/SMS Review Requests',
        has_insta_stream: 'Instagram Feed',
        has_lead_machine: 'Review Widgets',
      };
      return map[key] || key;
    },
  },
};
</script>

<style scoped>
.business-subscription-manager {
  max-width: 700px;
  margin: 0 auto;
  padding: 2rem 1rem;
  font-family: 'Segoe UI', Arial, sans-serif;
}
.title {
  text-align: center;
  margin-bottom: 2rem;
  color: #2c3e50;
}
.subscriptions-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.subscription-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07);
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  border: 1px solid #eaeaea;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s;
}
.subscription-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}
.plan-name {
  font-size: 1.2rem;
  font-weight: 600;
  color: #fff;
}
.status-badge {
  padding: 0.2em 0.8em;
  border-radius: 12px;
  font-size: 0.95em;
  font-weight: 500;
  text-transform: capitalize;
  background: #e0e0e0;
  color: #333;
}
.status-badge.active {
  background: #d1f7c4;
  color: #218838;
}
.status-badge.cancelled, .status-badge.inactive {
  background: #f8d7da;
  color: #721c24;
}
.card-body {
  margin-bottom: 1rem;
}
.description {
  margin-bottom: 0.7rem;
  color: #555;
}
.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1.5rem;
  font-size: 0.98em;
}
.card-footer {
  display: flex;
  justify-content: flex-end;
}
.cancel-btn {
  background: #ff4d4f;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.5em 1.2em;
  font-size: 1em;
  cursor: pointer;
  transition: background 0.2s;
}
.cancel-btn:disabled {
  background: #ffb3b3;
  cursor: not-allowed;
}
.cancel-btn:not(:disabled):hover {
  background: #d9363e;
}
.loading {
  text-align: center;
  font-size: 1.2em;
  color: #888;
  margin-top: 2rem;
}
.no-subscription {
  text-align: center;
  color: #888;
  margin-top: 2rem;
}
.modules-access {
  margin-top: 1.1rem;
}
.modules-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
  margin-top: 0.3em;
}
.module-badge {
  background: #f0f4fa;
  color: #2c3e50;
  border-radius: 12px;
  padding: 0.2em 0.9em;
  font-size: 0.95em;
  font-weight: 500;
  border: 1px solid #dbeafe;
}
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content {
  background: #fff;
  border-radius: 10px;
  padding: 2rem 2.5rem 1.5rem 2.5rem;
  box-shadow: 0 4px 24px rgba(0,0,0,0.18);
  max-width: 400px;
  width: 100%;
  text-align: center;
}
.modal-actions {
  display: flex;
  justify-content: center;
  gap: 1.2rem;
  margin-top: 1.5rem;
}
.modal-btn {
  padding: 0.5em 1.5em;
  border-radius: 6px;
  border: none;
  font-size: 1em;
  background: #eaeaea;
  color: #2c3e50;
  cursor: pointer;
  transition: background 0.2s;
}
.modal-btn.danger {
  background: #ff4d4f;
  color: #fff;
}
.modal-btn:disabled {
  background: #f5c6cb;
  color: #888;
  cursor: not-allowed;
}
</style>
